/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {IconButton, Tooltip} from "@mui/material";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {ArgosOpenInNewTabIcon as OpenInNewIcon} from "../../../../components/Icons/ArgosActionIcons";
import {EViewDataSetErrorsDialogDataType, openViewDataSetErrorsDialog} from "../../../../../redux/ducks/SensingSystemArchitectureDesign";
import {ARGOS_ERROR, ARGOS_GREEN, ARGOS_PENDING_COLOR, ARGOS_PROGRESS_COLOR, ARGOS_WARNING, tableButtonGray} from "../../../../../constants/colors";
import {MRT_Cell} from "material-react-table";
import {SensorBomTableData} from "./BillOfMaterialsSection/BillOfMaterialsSection";
import {GreenLineDataSetStates} from "../../types";
import {AppDispatch} from "../../../../../redux/store";
import PendingIcon from '@mui/icons-material/Pending';
import CircleIcon from '@mui/icons-material/Circle';
import Loader from "../../../../components/Loader/Loader";
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import React from "react";


// create return type for getStateCellRenderConfiguration
export type StateCellRenderConfiguration = {
  [key in GreenLineDataSetStates]: {
    color: string;
    icon: React.JSX.Element;
    extraContent: React.JSX.Element | null;
    tooltip: string;
  };
}
/**
 * Retrieves the cell rendering configuration associated with a specific Green Line Data Set state.
 *
 * @param dispatch - The Redux dispatch function used to trigger actions.
 * @param greenlineDatasetId - The unique identifier for the Green Line Data Set.
 * @param dataType - An enum indicating whether the type of data
 * @returns An object containing the properties for the specified state, including:
 * - `color`: The color associated with the state.
 * - `icon`: The icon representing the state.
 * - `extraContent`: Additional content to render for the state (if any).
 * - `tooltip`: A tooltip message describing the state.
 */
export const getStateCellRenderConfiguration = (dispatch: AppDispatch, greenlineDatasetId: string, dataType: EViewDataSetErrorsDialogDataType): StateCellRenderConfiguration => ({
  [GreenLineDataSetStates.NEW]: {
    color: ARGOS_PENDING_COLOR, icon: <CircleIcon />, extraContent: null,
    tooltip: 'Select a red line data set and objects to complete the data set configuration'
  },
  [GreenLineDataSetStates.PENDING]: {
    color: ARGOS_WARNING, icon: <WatchLaterIcon/>, extraContent: null,
    tooltip: 'Select and click Generate Green Lines to recompute the data set'
  },
  [GreenLineDataSetStates.PROCESSING]: {
    color: tableButtonGray, icon: <PendingIcon/>, extraContent: null,
    tooltip: 'Setting up data set for generation'
  },
  [GreenLineDataSetStates.GENERATING]: {
    color: ARGOS_PROGRESS_COLOR, icon: <Loader isLoading={true} loaderSize={20}/>, extraContent: null,
    tooltip: 'This may take several minutes to complete'
  },
  [GreenLineDataSetStates.COMPLETE]: {
    color: ARGOS_GREEN, icon: <CheckCircleIcon/>, extraContent: null,
    tooltip: 'Generation was successful and data is up-to-date'
  },
  [GreenLineDataSetStates.ERROR]: {
    color: ARGOS_ERROR, icon: <ErrorIcon/>,
    extraContent: (
      <Tooltip title='View Error Details'>
        <IconButton onClick={() => dispatch(openViewDataSetErrorsDialog({dataSetId: greenlineDatasetId, dataType: dataType}))}>
          <OpenInNewIcon style={{color: tableButtonGray}} />
        </IconButton>
      </Tooltip>
    ),
    tooltip: 'An error occurred and the data set could not be generated',
  },
})

/**
 * Renders a cell in the table that displays the state of a Green Line Data Set.
 *
 * @param cell - The Material React Table cell object containing data for the current row.
 * @param dispatch - The Redux dispatch function used to trigger actions.
 * @param renderedCellValue - The value to be displayed in the cell, typically the state name, from material-react-table.
 * @returns A React component that visually represents the state of the Green Line Data Set.
 *
 * The rendered cell includes:
 * - An icon and text representing the current state.
 * - A tooltip providing additional information about the state.
 * - Optional extra content, such as an error details button for the "ERROR" state.
 */
export const renderStateCell = (cell: MRT_Cell<SensorBomTableData>, dispatch: AppDispatch, renderedCellValue: React.ReactNode) => () => {
  const dataSetState = cell.row.original.state
  const greenlineDatasetId = cell.row.original.greenlineDatasetId
  if(!greenlineDatasetId || !dataSetState) {
    return ''
  }

  const {color, extraContent, icon, tooltip} = getStateCellRenderConfiguration(dispatch, greenlineDatasetId, EViewDataSetErrorsDialogDataType.GreenLine)[dataSetState]

  return (
    <ArgosFlexbox alignItems='center'>
      <Tooltip title={tooltip}>
        <ArgosFlexbox alignItems='center'>
          <div style={{color: color}}>
            {icon}
          </div>
          <div>{renderedCellValue}</div>
        </ArgosFlexbox>
      </Tooltip>
      {extraContent}
    </ArgosFlexbox>
  )
}

/**
 * Renders a cell in the table that displays the state of a Green Line Data Set.
 *
 * @param cell - The Material React Table cell object containing data for the current row.
 * @param dispatch - The Redux dispatch function used to trigger actions.
 * @param renderedCellValue - The value to be displayed in the cell, typically the state name, from material-react-table.
 * @returns A React component that visually represents the state of the Green Line Data Set.
 *
 * The rendered cell includes:
 * - An icon and text representing the current state.
 * - A tooltip providing additional information about the state.
 * - Optional extra content, such as an error details button for the "ERROR" state.
 */
export const renderBlueLineStateCell = (cell: MRT_Cell<SensorBomTableData>, dispatch: AppDispatch, renderedCellValue: React.ReactNode) => () => {
  const greenlineDatasetId = cell.row.original.greenlineDatasetId
  const dataSetState = cell.row.original.blueLineState?.state
  if(!greenlineDatasetId || !dataSetState) {
    return ''
  }
  const {color, extraContent, icon, tooltip} = getStateCellRenderConfiguration(dispatch, greenlineDatasetId, EViewDataSetErrorsDialogDataType.BlueLine)[dataSetState]

  return (
    <ArgosFlexbox alignItems='center'>
      <Tooltip title={tooltip}>
        <ArgosFlexbox alignItems='center'>
          <div style={{color: color}}>
            {icon}
          </div>
          <div>{renderedCellValue}</div>
        </ArgosFlexbox>
      </Tooltip>
      {extraContent}
    </ArgosFlexbox>
  )
}

/**
 * Renders a cell in the table that displays the state of a Green Line Data Set.
 *
 * @param cell - The Material React Table cell object containing data for the current row.
 * @param dispatch - The Redux dispatch function used to trigger actions.
 * @param renderedCellValue - The value to be displayed in the cell, typically the state name, from material-react-table.
 * @returns A React component that visually represents the state of the Green Line Data Set.
 *
 * The rendered cell includes:
 * - An icon and text representing the current state.
 * - A tooltip providing additional information about the state.
 * - Optional extra content, such as an error details button for the "ERROR" state.
 */
export const renderViolationsStateCell = (cell: MRT_Cell<SensorBomTableData>, dispatch: AppDispatch, renderedCellValue: React.ReactNode) => () => {
  const greenlineDatasetId = cell.row.original.greenlineDatasetId
  const dataSetState = cell.row.original.violationsState?.state
  if(!greenlineDatasetId || !dataSetState) {
    return ''
  }
  const {color, extraContent, icon, tooltip} = getStateCellRenderConfiguration(dispatch, greenlineDatasetId, EViewDataSetErrorsDialogDataType.Violations)[dataSetState]

  return (
    <ArgosFlexbox alignItems='center'>
      <Tooltip title={tooltip}>
        <ArgosFlexbox alignItems='center'>
          <div style={{color: color}}>
            {icon}
          </div>
          <div>{renderedCellValue}</div>
        </ArgosFlexbox>
      </Tooltip>
      {extraContent}
    </ArgosFlexbox>
  )
}
